import { getLevelDepth, useSupergridMutations } from '@aleph/supergrid';
import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { captureException } from '@sentry/react';
import { alephToast, NotificationToast, NotificationType } from '@aleph/shared-components';
import { IRowNode } from 'ag-grid-community';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import { SPOTIFY_SUPERGRID_CONFIG, transformSpotifyRows } from 'wb-spotify/app/routes/superGrid/config';
import { generateMutationHash } from 'wb-spotify/app/routes/superGrid/mutator/utils';
import { DuplicateEntityTreeDocument } from 'wb-spotify/graphql/generated';
import { LEVEL_ID_FILTER } from 'wb-spotify/app/routes/superGrid/actionRow/filters/utils';

const useDuplicateBulkAction = () => {
	const client = useApolloClient();
	const { addMutation } = useSupergridMutations();
	const [loading, setLoading] = useState(false);

	const onDuplicate = async (rowNodes: IRowNode[]) => {
		setLoading(true);

		try {
			const duplicateLevel = rowNodes[0]?.data?.level as SpotifyLevel;

			const duplicateFilters = {
				[LEVEL_ID_FILTER[duplicateLevel]]: [{
					value: rowNodes.map(rowNode => rowNode.data.id),
				}],
			};

			const { data } = await client.query({
				query:     DuplicateEntityTreeDocument,
				variables: {
					campaignFilters: duplicateFilters,
					groupFilters:    duplicateFilters,
					adFilters:       duplicateFilters,
				},
			});

			const duplicateLevelDepth = getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, duplicateLevel);
			const campaignLevelDepth = getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, SpotifyLevel.CAMPAIGN);
			const groupLevelDepth = getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, SpotifyLevel.GROUP);
			const adLevelDepth = getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, SpotifyLevel.AD);

			/**
             * Fixing this type error is not worth the effort since flattenObject makes it any type anyway
             */
			// @ts-ignore
			const campaignMutations = await transformSpotifyRows((data.entities?.campaigns ?? []).map(campaign => ({
				...campaign,
				id:   duplicateLevelDepth <= campaignLevelDepth ? undefined : campaign.id,
				hash: duplicateLevelDepth <= campaignLevelDepth ? generateMutationHash() : undefined,
				name: duplicateLevelDepth <= campaignLevelDepth ? `Duplicate of ${campaign.name}` : campaign.name,
			})), SpotifyLevel.CAMPAIGN);

			const groupMutations = await transformSpotifyRows(campaignMutations.flatMap(
				campaign => (campaign?.groups ?? []).map(group => ({
					...group,
					supergridLevel: SpotifyLevel.GROUP,
					parent:         campaign,
					id:             duplicateLevelDepth <= groupLevelDepth ? undefined : group.id,
					hash:           duplicateLevelDepth <= groupLevelDepth ? generateMutationHash() : undefined,
					name:           duplicateLevelDepth <= groupLevelDepth ? `Duplicate of ${group.name}` : group.name,
				})),
			), SpotifyLevel.GROUP);

			const adMutations = await transformSpotifyRows(groupMutations.flatMap(
				group => (group?.ads ?? []).map(ad => ({
					...ad,
					parent: group,
					id:     duplicateLevelDepth <= adLevelDepth ? undefined : ad.id,
					hash:   duplicateLevelDepth <= adLevelDepth ? generateMutationHash() : undefined,
					name:   duplicateLevelDepth <= adLevelDepth ? `Duplicate of ${ad.name}` : ad.name,
				})),
			), SpotifyLevel.AD);

			campaignMutations.filter(mutation => !!mutation.hash).forEach(
				campaignMutation => addMutation(SpotifyLevel.CAMPAIGN, campaignMutation),
			);
			groupMutations.filter(mutation => !!mutation.hash).forEach(
				groupMutation => addMutation(SpotifyLevel.GROUP, groupMutation),
			);
			adMutations.filter(mutation => !!mutation.hash).forEach(
				adMutation => addMutation(SpotifyLevel.AD, adMutation),
			);
		} catch (e) {
			captureException(e);

			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title="Error occured while duplicating selected entities"
					description="The entities you are trying to duplicate might be corrupted."
				/>,
			);
		}

		setLoading(false);
	};

	return {
		onDuplicate,
		loading,
	};
};

export default useDuplicateBulkAction;
